import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api, { setAccessToken, clearAccessToken, setSessionExpiredHandler, setSessionRefreshActivityProvider } from "../api/client";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const DEFAULT_IDLE_SECONDS = 30 * 60;
const SESSION_SEEN_KEY = "coalytix_had_session";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "visibilitychange"];

function applyEffectiveOrg(data) {
  const eff = data?.effective_org_slug || data?.user?.effective_org_slug;
  if (!eff) return;
  const prev = JSON.parse(localStorage.getItem("org") || "{}");
  const list = Array.isArray(prev.list) ? prev.list : [];
  const newList = Array.from(new Set([eff, ...list])).sort((a, b) => a.localeCompare(b));
  localStorage.setItem("org", JSON.stringify({ current: eff, list: newList }));
  window.dispatchEvent(new Event("org-changed"));
}

function normalizeUser(data, fallbackUid, previousUser) {
  const source = data?.user || data || {};
  const roles = Array.from(new Set(source.roles || data?.roles || [])).filter(Boolean);
  const preferredRole = previousUser?.currentRole && roles.includes(previousUser.currentRole)
    ? previousUser.currentRole
    : roles[0] || null;
  return {
    uid: source.uid || data?.uid || fallbackUid || "",
    email: source.email || data?.email || "",
    roles,
    currentRole: preferredRole,
    mustChange: !!(source.must_change ?? data?.must_change),
    effectiveOrg: source.effective_org || data?.effective_org || "",
    effectiveOrgSlug: source.effective_org_slug || data?.effective_org_slug || "",
  };
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const idleSecondsRef = useRef(DEFAULT_IDLE_SECONDS);
  const lastActivityRef = useRef(Date.now());
  const lastRefreshRef = useRef(0);
  const refreshInFlightRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const clearSessionState = useCallback((showExpiredModal = false) => {
    clearAccessToken();
    localStorage.removeItem("auth");
    localStorage.removeItem(SESSION_SEEN_KEY);
    setUser(null);
    setOtpChallenge(null);
    if (showExpiredModal) setSessionExpired(true);
  }, []);

  const restoreFromResponse = useCallback((data, fallbackUid) => {
    if (!data?.ok || !data?.access_token) return null;
    setAccessToken(data.access_token);
    if (data.idle_timeout_seconds) idleSecondsRef.current = Number(data.idle_timeout_seconds) || DEFAULT_IDLE_SECONDS;
    applyEffectiveOrg(data);
    const nextUser = normalizeUser(data, fallbackUid, userRef.current);
    setUser(nextUser);
    localStorage.removeItem("auth");
    localStorage.setItem(SESSION_SEEN_KEY, "1");
    lastRefreshRef.current = Date.now();
    return nextUser;
  }, []);

  const hasRecentUserActivityForRefresh = useCallback(() => {
    const now = Date.now();
    const idleLimitMs = Math.max(60, idleSecondsRef.current || DEFAULT_IDLE_SECONDS) * 1000;
    return (now - lastActivityRef.current) < idleLimitMs && lastActivityRef.current > lastRefreshRef.current;
  }, []);

  const refreshSession = useCallback(async ({ silent = true } = {}) => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;
    const activityHeaders = hasRecentUserActivityForRefresh() ? { "X-User-Activity": "1" } : {};
    refreshInFlightRef.current = api.post("/auth/refresh", null, {
      headers: activityHeaders,
      skipAuthHeader: true,
      skipAuthRefresh: true,
      skipSessionExpiredModal: silent,
    })
      .then((res) => restoreFromResponse(res.data))
      .finally(() => {
        refreshInFlightRef.current = null;
      });
    return refreshInFlightRef.current;
  }, [restoreFromResponse, hasRecentUserActivityForRefresh]);

  const expireSession = useCallback(async () => {
    try {
      await api.post("/auth/logout", null, {
        skipAuthHeader: true,
        skipAuthRefresh: true,
        skipSessionExpiredModal: true,
      });
    } catch {
      // best effort
    }
    clearSessionState(true);
  }, [clearSessionState]);

  useEffect(() => {
    setSessionExpiredHandler(() => expireSession());
    setSessionRefreshActivityProvider(hasRecentUserActivityForRefresh);
    return () => {
      setSessionExpiredHandler(null);
      setSessionRefreshActivityProvider(null);
    };
  }, [expireSession, hasRecentUserActivityForRefresh]);

  useEffect(() => {
    let mounted = true;
    setInitializing(true);
    localStorage.removeItem("auth");
    api.post("/auth/refresh", null, {
      headers: { "X-User-Activity": "1" },
      skipAuthHeader: true,
      skipAuthRefresh: true,
      skipSessionExpiredModal: true,
    })
      .then((res) => {
        if (!mounted) return;
        restoreFromResponse(res.data);
      })
      .catch(() => {
        if (!mounted) return;
        const hadSession = localStorage.getItem(SESSION_SEEN_KEY) === "1";
        const publicPath = ["/login", "/forgot-password"].includes(window.location.pathname);
        clearSessionState(hadSession && !publicPath);
      })
      .finally(() => {
        if (mounted) setInitializing(false);
      });
    return () => {
      mounted = false;
    };
  }, [clearSessionState, restoreFromResponse]);

  useEffect(() => {
    const markActivity = () => {
      if (document.visibilityState === "hidden") return;
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((eventName) => window.removeEventListener(eventName, markActivity));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const id = window.setInterval(() => {
      const now = Date.now();
      const idleMs = now - lastActivityRef.current;
      const idleLimitMs = Math.max(60, idleSecondsRef.current || DEFAULT_IDLE_SECONDS) * 1000;
      if (idleMs >= idleLimitMs) {
        expireSession();
        return;
      }
      const refreshEveryMs = Math.min(5 * 60 * 1000, Math.max(60 * 1000, idleLimitMs / 3));
      const hasActivitySinceRefresh = lastActivityRef.current > lastRefreshRef.current;
      if (hasActivitySinceRefresh && now - lastRefreshRef.current >= refreshEveryMs) {
        refreshSession({ silent: false }).catch(() => expireSession());
      }
    }, 30 * 1000);
    return () => window.clearInterval(id);
  }, [user, refreshSession, expireSession]);

  const login = async (uid, password) => {
    setLoading(true);
    setSessionExpired(false);
    setOtpChallenge(null);
    try {
      const stored = JSON.parse(localStorage.getItem("org") || "{}");
      const currentOrg = stored?.current;
      const body = new URLSearchParams({ uid, password });
      const res = await api.post("/auth/login", body, {
        params: currentOrg ? { org: currentOrg } : {},
        skipAuthHeader: true,
        skipAuthRefresh: true,
        skipSessionExpiredModal: true,
      });
      const data = res.data || {};
      if (!data.ok) return { ok: false, error: data.error || "Invalid credentials" };
      if (data.otp_required) {
        setOtpChallenge(data);
        return { ok: true, otpRequired: true, challenge: data };
      }
      restoreFromResponse(data, uid);
      const now = Date.now();
      lastActivityRef.current = now;
      lastRefreshRef.current = now;
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.error || "Invalid credentials" };
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginOtp = async (challengeId, otp) => {
    setLoading(true);
    setSessionExpired(false);
    try {
      const body = new URLSearchParams({ challenge_id: challengeId, otp });
      const res = await api.post("/auth/login/verify_otp", body, {
        skipAuthHeader: true,
        skipAuthRefresh: true,
        skipSessionExpiredModal: true,
      });
      const data = res.data || {};
      if (!data.ok) return { ok: false, error: data.error || "Invalid OTP" };
      restoreFromResponse(data);
      setOtpChallenge(null);
      const now = Date.now();
      lastActivityRef.current = now;
      lastRefreshRef.current = now;
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.error || "Invalid or expired OTP" };
    } finally {
      setLoading(false);
    }
  };

  const resendLoginOtp = async (challengeId) => {
    setLoading(true);
    setSessionExpired(false);
    try {
      const body = new URLSearchParams({ challenge_id: challengeId });
      const res = await api.post("/auth/login/resend_otp", body, {
        skipAuthHeader: true,
        skipAuthRefresh: true,
        skipSessionExpiredModal: true,
      });
      const data = res.data || {};
      if (!data.ok) return { ok: false, error: data.error || "Could not resend OTP" };
      setOtpChallenge((current) => ({ ...(current || {}), ...data }));
      return { ok: true, challenge: data };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.error || "Could not resend OTP" };
    } finally {
      setLoading(false);
    }
  };

  const clearOtpChallenge = () => setOtpChallenge(null);

  const logout = async () => {
    try {
      await api.post("/auth/logout", null, {
        skipAuthHeader: true,
        skipAuthRefresh: true,
        skipSessionExpiredModal: true,
      });
    } catch {
      // best effort
    }
    setSessionExpired(false);
    clearSessionState(false);
  };

  const switchRole = (role) => {
    if (!user || !user.roles.includes(role)) return;
    setUser({ ...user, currentRole: role });
  };

  const visibleRoles = (u) => {
    const target = u || user;
    if (!target || !Array.isArray(target.roles)) return [];
    return Array.from(new Set(target.roles));
  };

  const closeExpiredAndLogin = () => {
    setSessionExpired(false);
    clearSessionState(false);
    window.location.assign("/login");
  };

  return (
    <AuthContext.Provider value={{ user, loading, initializing, sessionExpired, otpChallenge, login, verifyLoginOtp, resendLoginOtp, clearOtpChallenge, logout, switchRole, setUser, visibleRoles, refreshSession }}>
      {children}
      {sessionExpired ? (
        <div className="session-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
          <div className="session-expired-modal">
            <h2 id="session-expired-title">Session expired</h2>
            <p>Your session has expired due to inactivity. Please log in again.</p>
            <button className="btn btn-primary" type="button" onClick={closeExpiredAndLogin}>Login again</button>
          </div>
        </div>
      ) : null}
    </AuthContext.Provider>
  );
}
